// src/rms/data/permissions.js
// ============================================================================
// What each role may do
// ============================================================================
// The client's copy of a decision the DATABASE owns. Every capability here has
// a counterpart in SQL — rms_can_manage_money / rms_can_manage_ops /
// rms_can_manage_staff, and the RESTRICTIVE policies in
// 20260820090001_rms_role_policies.sql — and the SQL is the one that decides.
// This module exists so screens can hide what a person cannot use, which is
// courtesy, not security. Somebody who edits it in devtools reaches a form
// whose save is refused by Postgres.
//
// Keeping it in one plain module rather than scattered through components is
// the point: these are two encodings of one decision, nothing keeps them in
// step automatically, and they have to be reviewed side by side. If you change
// a line here, open the migration.
//
// ----------------------------------------------------------------------------
// Two axes, not a ladder
// ----------------------------------------------------------------------------
// A ladder assumes seniority buys more of everything, which does not describe
// a bookkeeper: they want the invoices and the tax rates, and they have no
// business handing a vehicle across a counter.
//
//                money   ops     staff   pricing  agreements
//   owner         yes     yes     yes     yes      yes
//   manager       yes     yes     no      no       no
//   bookkeeper    yes     no      no      yes      no
//   staff         no      yes     no      no       no
//
// `pricing` is a fourth axis rather than a rung, and it is the one that reads
// oddly at first glance: a manager has every other money right and not this
// one. That is deliberate. A manager runs the floor -- takes payment, issues a
// refund, reads the billing screen -- and what the business CHARGES is not a
// floor decision. It sits with the owner and the bookkeeper.
//
// `agreements` is a fifth, and narrower still: the bookkeeper is out too. The
// contract is what decides who pays for a wrecked vehicle, and rewriting it is
// not a pricing decision or a floor decision. Reading it stays open to
// everybody -- staff hand it to customers and answer questions about it.
// ============================================================================

export const ROLES = ["owner", "manager", "bookkeeper", "staff"];

/** Ordered for a picker: the most trusted first, matching how one is chosen. */
export const ROLE_OPTIONS = [
  { value: "staff", labelKey: "role.staff", label: "Staff" },
  { value: "manager", labelKey: "role.manager", label: "Manager" },
  { value: "bookkeeper", labelKey: "role.bookkeeper", label: "Bookkeeper" },
  { value: "owner", labelKey: "role.owner", label: "Owner" },
];

const NONE = {
  money: false,
  ops: false,
  staff: false,
  pricing: false,
  openSettings: false,
  addAsset: false,
  manageAgreements: false,
  managePricing: false,
  manageTax: false,
  viewBilling: false,
  reverseMoney: false,
  exportData: false,
  manageStaff: false,
  changePlan: false,
  deleteBusiness: false,
  viewRevenue: false,
};

/**
 * Capabilities for a role.
 *
 * An unknown or missing role gets nothing. That is deliberate: the failure
 * mode of a typo should be a screen that hides things, not one that shows
 * everything to somebody whose role could not be resolved.
 */
export function capabilitiesFor(role) {
  const money = role === "owner" || role === "manager" || role === "bookkeeper";
  const ops = role === "owner" || role === "manager" || role === "staff";
  const staff = role === "owner";
  const pricing = role === "owner" || role === "bookkeeper";

  if (!ROLES.includes(role)) return { ...NONE };

  return {
    // The four axes, mirroring the SQL predicates by name so a reader can
    // grep one and find the other.
    money,
    ops,
    staff,
    pricing,

    // Named capabilities, so screens read as intent rather than as arithmetic.

    // The rate card, add-ons, usage fees, cancellation terms. Narrower than
    // money and ENFORCED: rms_can_manage_pricing plus RESTRICTIVE policies on
    // the four tables, in 20260821090000.
    managePricing: pricing,

    manageTax: money,
    viewBilling: money,
    reverseMoney: money,

    // Export is UI-gated ONLY and cannot be enforced in RLS: a staff member can
    // already open the customer list and the booking list on screen, so a file
    // of the same rows is a convenience rather than a new capability. Revoking
    // staff SELECT to protect the export would revoke the job. Said here as
    // well as in the migration so neither reader has to guess.
    exportData: money,

    // Revenue AGGREGATES: dashboard totals, monthly recurring, outstanding.
    // Not the price of the booking in front of you — staff cannot work a
    // counter blind to what they are charging, and that stays visible to
    // everyone. Enforced for real, because the figures are fetched from a
    // SECURITY DEFINER function rather than hidden after loading.
    viewRevenue: money,

    // Putting a vehicle on the fleet. Money rather than ops, because it is a
    // purchase before it is a counter operation -- and ops includes staff,
    // which is exactly who is excluded. ENFORCED: RESTRICTIVE INSERT policy on
    // rms_assets, plus a signature trigger.
    addAsset: money,

    // Opening the Settings section. ATTRIBUTION, not concealment: the operator
    // record is already loaded on every screen by RmsContext, so nothing there
    // is secret from somebody with devtools. What the gate buys is a shared
    // counter session stopping at a name, and a row saying who went in. The
    // WRITES behind it are the real control, and always were --
    // rms_operators_update is USING (user_id = auth.uid()).
    openSettings: staff,

    manageStaff: staff,
    changePlan: staff,
    deleteBusiness: staff,

    // Rewriting the contract customers sign. Owner alone, and a fifth axis
    // rather than a reuse of `staff`, mirroring rms_can_manage_agreements by
    // name so grepping one finds the other. ENFORCED: the write policies on
    // rms_agreement_templates in 20260821160000 name that predicate, and a
    // change to the WORDING additionally needs a signed PIN.
    manageAgreements: staff,
  };
}

export default { ROLES, ROLE_OPTIONS, capabilitiesFor };
